/**
 * useSaveBeforeDecision — „najpierw normalny zapis, potem decyzja" na karcie rekordu w edycji
 * (decyzja właściciela 2026-10-05/06; jak „Akceptuję" w starym WF).
 *
 * Baner procesu nad formularzem pokazuje przyciski decyzji także w trybie edycji. Reguła: gdy
 * formularz jest w edycji, klik w którykolwiek przycisk decyzji NAJPIERW wykonuje zwykły zapis
 * formularza (to samo co „Zapisz": ta sama walidacja, to samo żądanie, to samo ostrzeżenie
 * procedury kontrolnej), a okno decyzji otwiera się DOPIERO po udanym zapisie. Jeśli zapis
 * zatrzyma walidacja w panelu, błąd serwera albo anulowane ostrzeżenie kontrolne — okno się nie
 * otwiera, a formularz zostaje tak, jak zostawia go zwykły nieudany zapis. Poza edycją
 * (`beforeDecision === undefined`) okno otwiera się od razu.
 *
 * Wiązanie (formularz):
 *   - `saveMutationKey` → `mutationKey` mutacji zapisu (`useMutation`); po nim hak sprawdza NA
 *                        ŻYWO, czy zapis TEJ kopii formularza trwa (kliknięte „Zapisz") — wtedy
 *                        nie startuje drugiego, a okno otworzy się po wyniku tamtego,
 *   - `beforeDecision` → prop banera (`ProcessApprovalBanner` → `ApprovalActionsPanel` oraz, przy
 *                        wolnym slocie, `SubmitForApprovalButton` jako `beforeSubmit`) i TA SAMA
 *                        bramka dla elementów układu formularza „Proces akceptacji” (przyciski
 *                        decyzji) oraz „Przycisk akceptacji” — strona rekordu podaje ją wszystkim,
 *   - `succeeded()`    → na końcu `onSuccess` zapisu w EDYCJI (nie przy tworzeniu), w tym samym
 *                        synchronicznym kroku co wyjście z edycji — bez `await` między
 *                        `setEditing(false)` a `succeeded()`; inaczej bezpiecznik wyjścia z edycji
 *                        zdąży zapomnieć okno. Przed obydwoma formularz musi już mieć ODŚWIEŻONY
 *                        rekord (typ / kategoria, z których baner bierze listę procesów): w
 *                        `onSuccess` czeka (`await`) na `invalidateQueries` samego rekordu, dopiero
 *                        potem `setEditing(false)` + `succeeded()`. Inaczej okno wysyłki do
 *                        akceptacji otworzyłoby się z listą procesów STAREGO typu / kategorii
 *                        (albo przycisk zniknąłby bez dymku). Pozostałe odświeżenia — bez czekania,
 *   - `aborted()`      → na początku `onError` zapisu (poza `CONTROL_WARNING`, o którym rozstrzyga
 *                        dialog) oraz jako `onCancel` hooka `useControlWarning` (anulowane ostrzeżenie).
 *
 * Blokada przycisków na czas zapisu (decyzja właściciela 2026-10-07): `beforeDecision` to funkcja
 * (bramka), która niesie jeszcze dwie flagi WYGLĄDU — `saving` (zapis tej kopii formularza trwa:
 * zwykłe „Zapisz" albo zapis z kliknięcia decyzji) i `waiting` (okno decyzji czeka na wynik zapisu
 * uruchomionego kliknięciem decyzji). Baner blokuje wtedy przyciski decyzji (`saving || waiting`),
 * a kręciołek pokazuje tylko ten kliknięty (`waiting`); zwykły „Zapisz" blokuje bez kręciołka.
 * Flagi trafiają do banera tym samym propem co bramka — strony nie wiedzą o nich nic i nie zmieniają
 * się. Tę samą bramkę z flagami dostaje też przycisk „Wyślij do akceptacji" (`SubmitForApprovalButton`,
 * prop `beforeSubmit`): szarzeje w trakcie zwykłego „Zapisz", a okno wyboru procesu otwiera się po
 * udanym zapisie.
 * To WYŁĄCZNIE wygląd: `useIsMutating` i stan renderu odświeżają się asynchronicznie, więc o tym,
 * czy wysłać drugi zapis, nadal decyduje bramka — na żywym `isMutating` i refach, nie na flagach.
 *
 * Wynik wywołania bramki (`true` / `false`) mówi wołającemu, czy bramka ZACZĘŁA czekać na zapis dla
 * TEGO kliknięcia: `true` = czeka (okno otworzy się po `succeeded()`), `false` = nie czeka (okno
 * otwarte od razu, walidacja w panelu zatrzymała zapis, albo kliknięcie zignorowane, bo zapis z
 * wcześniejszego kliknięcia jeszcze trwa; gdy `save` rzuci, wyjątek leci dalej zamiast wyniku).
 * Dzięki temu wołający zapamiętuje klikniętą drogę (kręciołek) tylko dla kliknięcia, które naprawdę
 * czeka. Drugie kliknięcie w trakcie oczekiwania jest IGNOROWANE: okno zostaje to z pierwszego
 * kliknięcia (nie podmieniamy go), a kręciołek zostaje na pierwszym przycisku.
 *
 * Dlaczego klucz, a nie `isPending` z wyniku `useMutation`: TanStack zmienia status mutacji
 * dopiero po `onSuccess` / `onError`, a komponent widzi to w kolejnym renderze — w tej przerwie
 * migawka z renderu kłamie. Klucz jest unikalny dla kopii formularza (`useId`) i rekordu: ten
 * sam rekord otwarty naraz w karcie i w oknie wyboru nie dzieli stanu „zapis trwa". Zmiana
 * rekordu zmienia klucz, a TanStack odpina wtedy trwający zapis od formularza: jego odświeżenia
 * danych dotyczą poprzedniego rekordu (id z jego renderu), a przycisk „Zapisz" nowego rekordu
 * odblokowuje się od razu. `succeeded()` / `aborted()` z renderu poprzedniego rekordu hak
 * ignoruje — wynik starego zapisu nie otwiera ani nie gasi okna nowego rekordu.
 *
 * Hook trzyma w refach zapamiętaną funkcję otwarcia okna, najnowszy `save`, bieżący rekord
 * i znacznik „zapis właśnie się udał"; jedyny własny stan renderu to `waiting` (lustro refa z
 * oknem, tylko do wyglądu). Bezpiecznik: wyjście z edycji albo zmiana rekordu zapomina okno,
 * które jeszcze czeka (i gasi `waiting`).
 */

import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';

/**
 * Bramka przed otwarciem okna decyzji: woła się ją z funkcją otwarcia okna (`gate(open)`).
 *
 * Wynik wywołania: `true` = bramka zaczęła czekać na zapis dla TEGO kliknięcia (okno otworzy się
 * później, po udanym zapisie); `false` = nie czeka (okno otwarte od razu, zapis zatrzymany albo
 * kliknięcie zignorowane). `undefined` (zwykła funkcja bez wyniku, np. szpieg w teście) = bramka
 * nie raportuje — wołający traktuje to jak dotąd, czyli jak `true`: pamięta klikniętą drogę, a to,
 * czy kręciołek się pokaże, rozstrzygają wyłącznie flagi niżej. Tylko jawne `false` znaczy „nie czeka".
 *
 * Flagi wyglądu są opcjonalne, więc zwykła funkcja też jest poprawną bramką, a brak flag znaczy
 * „nic nie trwa".
 */
export type DecisionGate = ((open: () => void) => boolean | void) & {
  /** Zapis tej kopii formularza trwa (zwykłe „Zapisz" albo zapis z kliknięcia decyzji) — przyciski decyzji zablokowane. */
  readonly saving?: boolean;
  /** Okno decyzji czeka na wynik zapisu z kliknięcia decyzji — kliknięty przycisk pokazuje kręciołek. */
  readonly waiting?: boolean;
};

export interface UseSaveBeforeDecisionOptions {
  /** Czy formularz jest w trybie edycji. */
  editing: boolean;
  /** Normalny zapis formularza (to samo co „Zapisz”); zwraca, czy zapis wystartował (false = zatrzymała go walidacja w panelu). */
  save: () => boolean;
  /** Identyfikator rekordu formularza — zmiana (ta sama strona, inny rekord) zapomina czekające okno. */
  recordId: string | null | undefined;
}

export interface UseSaveBeforeDecisionResult {
  /** Do propsa banera; `undefined` poza edycją — okno decyzji otwiera się od razu. */
  beforeDecision: DecisionGate | undefined;
  /**
   * Woła formularz po udanym zapisie w edycji — otwiera zapamiętane okno decyzji (no-op, gdy nic nie czeka).
   * Wymóg: rekord (typ / kategoria) jest już ODŚWIEŻONY, a wyjście z edycji i to wywołanie idą razem,
   * w jednym synchronicznym kroku (patrz opis haka wyżej).
   */
  succeeded: () => void;
  /** Woła formularz, gdy zapis się nie udał albo anulowano ostrzeżenie — zapomina okno. */
  aborted: () => void;
  /** Klucz do `useMutation({ mutationKey })` zapisu formularza — unikalny dla tej kopii formularza i rekordu. */
  saveMutationKey: readonly ['save-before-decision', string, string | null];
}

export function useSaveBeforeDecision({
  editing,
  save,
  recordId,
}: UseSaveBeforeDecisionOptions): UseSaveBeforeDecisionResult {
  const queryClient = useQueryClient();
  const instanceId = useId();
  const currentRecordId = recordId ?? null;
  const saveMutationKey = useMemo(
    () => ['save-before-decision', instanceId, currentRecordId] as const,
    [instanceId, currentRecordId],
  );

  // Wygląd (NIE bramka): zapis tej kopii formularza trwa. `useIsMutating` odświeża się
  // asynchronicznie, więc służy tylko do blokady przycisków — nie do decyzji o drugim zapisie.
  const saving = useIsMutating({ mutationKey: saveMutationKey, exact: true }) > 0;

  // Funkcja otwarcia okna czekająca na wynik zapisu wywołanego kliknięciem decyzji;
  // `null` = żaden taki zapis nie trwa.
  const pendingOpenRef = useRef<(() => void) | null>(null);
  // Lustro powyższego refa w stanie renderu — wyłącznie do wyglądu (kręciołek na klikniętym
  // przycisku); ustawiane i zerowane zawsze razem z refem, przez `setPendingOpen`.
  const [waiting, setWaiting] = useState(false);
  const setPendingOpen = useCallback((open: (() => void) | null): void => {
    pendingOpenRef.current = open;
    setWaiting(open !== null);
  }, []);
  // Zapis w edycji właśnie się udał, a formularz za chwilę (w kolejnym renderze) wyjdzie
  // z edycji. Klik decyzji w tej przerwie zachowuje się jak poza edycją: okno od razu, bez
  // zbędnego drugiego zapisu tych samych danych.
  const justSucceededRef = useRef(false);

  // Najnowszy zapis formularza i bieżący rekord z ostatniego zatwierdzonego renderu: klik woła
  // AKTUALNY `save` (bez przestarzałego domknięcia), a sama bramka ma stałą tożsamość, dopóki nie
  // zmieni się tryb, rekord ani flagi wyglądu. Efekt układu biegnie przed malowaniem. (Opcje samej
  // mutacji TanStack przepisuje do obserwatora osobno, w efekcie pasywnym.)
  const saveRef = useRef(save);
  const recordIdRef = useRef(currentRecordId);
  useLayoutEffect(() => {
    saveRef.current = save;
    recordIdRef.current = currentRecordId;
  });

  // Bezpiecznik: okno czekające na zapis nie przeżywa wyjścia z edycji ani zmiany rekordu (ta
  // sama strona pokazuje inny rekord). Efekt biegnie po zatwierdzeniu renderu — udany zapis
  // woła `succeeded()` wcześniej, w `onSuccess`, więc tamtego okna to nie gasi.
  useEffect(() => {
    justSucceededRef.current = false;
    if (!editing) setPendingOpen(null);
  }, [editing, setPendingOpen]);
  useEffect(() => {
    justSucceededRef.current = false;
    setPendingOpen(null);
  }, [currentRecordId, setPendingOpen]);

  const beforeDecision = useMemo((): DecisionGate | undefined => {
    if (!editing) return undefined;
    const gate = (open: () => void): boolean => {
      if (justSucceededRef.current) {
        open();
        return false;
      }
      // Zapis z poprzedniego kliknięcia jeszcze trwa — kolejne kliknięcie IGNORUJEMY: okno zostaje
      // to z pierwszego kliknięcia (nie podmieniamy go), drugiego zapisu też nie wysyłamy.
      if (pendingOpenRef.current !== null) return false;
      setPendingOpen(open);
      let started = false;
      try {
        // Zapis tej kopii formularza już trwa (kliknięte „Zapisz") — czekamy na jego wynik.
        started =
          queryClient.isMutating({ mutationKey: saveMutationKey, exact: true }) > 0 ||
          saveRef.current();
      } finally {
        // Walidacja w panelu zatrzymała zapis (albo `save` rzucił) — nie ma na co czekać.
        if (!started) setPendingOpen(null);
      }
      return started;
    };
    return Object.assign(gate, { saving, waiting });
  }, [editing, queryClient, saveMutationKey, setPendingOpen, saving, waiting]);

  // Związane z rekordem renderu: wywołanie z domknięcia poprzedniego rekordu (wynik zapisu
  // odpiętego przy zmianie rekordu) nic nie robi.
  const succeeded = useCallback((): void => {
    if (recordIdRef.current !== currentRecordId) return;
    justSucceededRef.current = true;
    const open = pendingOpenRef.current;
    setPendingOpen(null);
    open?.();
  }, [currentRecordId, setPendingOpen]);

  const aborted = useCallback((): void => {
    if (recordIdRef.current !== currentRecordId) return;
    setPendingOpen(null);
  }, [currentRecordId, setPendingOpen]);

  return { beforeDecision, succeeded, aborted, saveMutationKey };
}
